// reactstrap components
import {
  Card,
  CardHeader,
  Input,
  Media,
  Container,
  Row,
} from "reactstrap";
import { useState, useEffect } from "react";
// core components
import Header from "components/Headers/Header.js";
import { useHistory } from "react-router-dom";

//data
import { GAMES_API, GAME_UPDATE_API } from "../../../services/CONSTANTS";
import {
  getApiDataService,
  postApiImageUploadService,
} from "../../../services/apiServices";
import { DataTable, FilterBar } from "components/shared";

// Status sits right after the name so it stays on screen on narrow viewports,
// where the table scrolls horizontally.
const GAME_COLUMNS = [
  { key: "index", label: "#" },
  { key: "game", label: "Game" },
  { key: "status", label: "Status" },
  { key: "unit", label: "Unit" },
  { key: "photo", label: "Photo" },
  { key: "actions", label: "" },
];

const GameList = () => {
  const history = useHistory();
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchInitData();
  }, []);

  function fetchInitData() {
    setIsLoading(true);
    console.log("GAMES_API=", GAMES_API);
    getApiDataService(GAMES_API)
      .then((res) => {
        console.log("res.data=", res.data);
        setGames(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("Error in useEffect!", err);
        setIsLoading(false);
        setGames([]);
      });
    return () => {
      console.log("axios cleanup.");
    };
  }

  const handleStatus = (e) => {
    setStatus(e.target.value);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const applyStatus = (id, value) =>
    setGames((current) =>
      current.map((item) =>
        item?.id === id ? { ...item, status: value } : item
      )
    );

  // Status is the customer-facing show/hide switch, so the row flips
  // optimistically and rolls back if the update is rejected.
  const handleGameStatus = (game, nextStatus) => {
    if (!game?.id || nextStatus === game.status) return;
    const previousStatus = game.status;
    setSavingId(game.id);
    applyStatus(game.id, nextStatus);

    const fd = new FormData();
    fd.append("status", nextStatus);
    // The update validator requires these two flags to be present, so resend
    // the game's current values — everything else is left untouched.
    fd.append("is_show_zone", game?.is_show_zone ? "true" : "false");
    fd.append("is_direct_topup", game?.is_direct_topup ? "true" : "false");
    postApiImageUploadService(GAME_UPDATE_API(game.id), fd)
      .then(() => {
        setSavingId(null);
      })
      .catch((err) => {
        console.log("Error updating game status!", err);
        applyStatus(game.id, previousStatus);
        setSavingId(null);
      });
  };

  const handleReset = () => {
    setStatus("");
    setSearch("");
  };

  // Client side filter — the dashboard games endpoint returns every game.
  const keyword = search.trim().toLowerCase();
  const filteredGames = games.filter((game) => {
    if (status && game?.status !== status) return false;
    if (!keyword) return true;
    return (
      game?.name?.toLowerCase().includes(keyword) ||
      game?.unit_name?.toLowerCase().includes(keyword)
    );
  });

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7 akh-page-content" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <FilterBar
                  title="Game Lists"
                  onClear={handleReset}
                  isFiltering={status !== "" || search !== ""}
                >
                  <Input
                    type="text"
                    className="akh-filter-width"
                    placeholder="Search"
                    aria-label="Search games"
                    value={search}
                    onChange={handleSearch}
                  />
                  <Input
                    className="akh-filter-select"
                    type="select"
                    value={status}
                    onChange={handleStatus}
                    aria-label="Filter by status"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="disable">Disable</option>
                  </Input>
                </FilterBar>
              </CardHeader>
              <DataTable
                columns={GAME_COLUMNS}
                data={filteredGames}
                isLoading={isLoading}
                emptyMessage="No records found"
                renderRow={(game, index) => (
                  <tr key={game?.id ?? index}>
                    <th className="akh-row-index">{index + 1}</th>
                    <th scope="row" className="akh-game-name-cell">
                      <Media className="align-items-center">
                        <a
                          className="mr-3 avatar rounded-circle"
                          href="#pablo"
                          onClick={(e) => e.preventDefault()}
                        >
                          <img
                            alt="..."
                            src={game?.unit_image}
                            style={{ width: "50px" }}
                          />
                        </a>
                        <Media>
                          <span className="mb-0 text-sm">{game?.name}</span>
                        </Media>
                      </Media>
                    </th>
                    <td>
                      <Input
                        type="select"
                        bsSize="sm"
                        className={`akh-status-select akh-status-select--${
                          game?.status === "active" ? "active" : "disable"
                        }`}
                        value={game?.status ?? ""}
                        disabled={savingId === game?.id}
                        onChange={(e) => handleGameStatus(game, e.target.value)}
                        aria-label={`Status for ${game?.name}`}
                      >
                        <option value="active">Active</option>
                        <option value="disable">Disable</option>
                      </Input>
                    </td>
                    <td>{game?.unit_name}</td>
                    <td>
                      <img
                        alt="..."
                        className="akh-game-thumb"
                        src={game?.thumbnail}
                      />
                    </td>
                    <td className="text-right">
                      <div className="row">
                        <div className="col">
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => {
                              history.push(`/admin/edit-game/${game.id}`);
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              />
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default GameList;
